/**
 * FuarAlaniFormPage.jsx — Fuar Alanı Oluştur / Düzenle (backlog #26, 2026-09-15)
 * Route: /Management/FairAreas/new  ·  /Management/FairAreas/:fuarAlaniId/edit
 *
 * Modal değil, ayrı sayfa. Fuar alanının salonları bu sayfada eklenir ve yönetilir:
 *  · Oluşturma — salonlar yerel bir listeye eklenir; "Kaydet" önce fuar alanını
 *    (POST /fuar/alani) sonra her salonu (POST /fuar/alani/{id}/salonlar) oluşturur.
 *  · Düzenle — ad değişikliği (PUT /fuar/alani/{id}) + canlı salon kartı
 *    (FuarAlaniSalonlariCard — tavan ve izin verilen yükseklik dahil).
 *
 * Backlog #51 (2026-09-20): WAS also the route for :fuarAlaniId (detay+düzenle birlikte).
 * Detay artık FuarAlaniDetayPage'de; bu sayfa yalnızca oluşturma/düzenleme.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Button, Input, InputGroup, InputGroupText,
  Label, Table, Spinner, Alert,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import FuarAlaniSalonlariCard from "../../../Components/FairAreas/FuarAlaniSalonlariCard";
import {
  getFuarAlani, createFuarAlani, renameFuarAlani, addSalon,
} from "../../../services/fuarAlaniSalonService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const EMPTY_HALL = { salonName: "", ceilingHeight: "", allowedHMax: "" };

export default function FuarAlaniFormPage() {
  const { t: tx } = useTranslation("yonetim");
  const { fuarAlaniId } = useParams();
  const isNew = !fuarAlaniId;
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState("");
  const [savedName, setSavedName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(location.state?.successMsg ?? null);
  const [pendingHalls, setPendingHalls] = useState([]); // yalnızca oluşturma modunda
  const [hallDraft, setHallDraft] = useState(EMPTY_HALL);

  useEffect(() => {
    document.title = i18n.t("yonetim:fuarAlaniForm.fds", { v: isNew ? i18n.t("yonetim:fuarAlaniForm.yeniFuarAlani") : i18n.t("yonetim:fuarAlaniForm.fuarAlani") });
  }, [isNew]);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (isNew) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const alani = await getFuarAlani(fuarAlaniId);
        if (!ignore) {
          setName(alani?.fuarAlaniName ?? "");
          setSavedName(alani?.fuarAlaniName ?? "");
        }
      } catch (e) {
        if (!ignore) setError(e.status === 404 ? i18n.t("yonetim:fuarAlaniForm.fuarAlaniBulunamadi") : e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarAlaniId, isNew]);

  const draftProblem = hallDraft.ceilingHeight !== "" && hallDraft.allowedHMax !== ""
    && Number(hallDraft.allowedHMax) > Number(hallDraft.ceilingHeight)
    ? tx("fuarAlaniForm.izinVerilenYukseklikFizikselTavandan") : null;

  function addPendingHall() {
    const salonName = hallDraft.salonName.trim();
    if (!salonName || draftProblem) return;
    if (pendingHalls.some((h) => h.salonName.toLocaleLowerCase("tr-TR") === salonName.toLocaleLowerCase("tr-TR"))) {
      setError(tx("fuarAlaniForm.listedeZatenVar", { salonName }));
      return;
    }
    setPendingHalls((p) => [...p, { ...hallDraft, salonName }]);
    setHallDraft(EMPTY_HALL);
  }

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      if (!isNew) {
        await renameFuarAlani(fuarAlaniId, trimmed);
        setSavedName(trimmed);
        setSuccessMsg(tx("fuarAlaniForm.fuarAlaniKaydedildi"));
        return;
      }
      const created = await createFuarAlani(trimmed);
      const newId = created?.fuarAlaniId;
      if (newId == null) throw new Error(tx("fuarAlaniForm.fuarAlaniOlusturulduAmaId"));
      // Salonlar sırayla: biri reddedilirse (ör. ad çakışması) diğerleri yine denensin.
      const failed = [];
      for (const h of pendingHalls) {
        try {
          await addSalon(newId, h);
        } catch (e) {
          failed.push(`${h.salonName}: ${e.message}`);
        }
      }
      const msg = failed.length
        ? tx("fuarAlaniForm.fuarAlaniOlusturulduAncakSalon", { length: failed.length, v: failed.join(" · ") })
        // WAS: : `Fuar alanı${pendingHalls.length ? ` ve ${pendingHalls.length} salon` : ""} oluşturuldu.`;
        : pendingHalls.length
          ? tx("fuarAlaniForm.fuarAlaniVeSalonlarOlusturuldu", { sayi: pendingHalls.length }) // #349
          : tx("fuarAlaniForm.fuarAlaniOlusturuldu");
      navigate(`/Management/FairAreas/${newId}`, { replace: true, state: { successMsg: msg } });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const title = isNew ? tx("fuarAlaniForm.yeniFuarAlani") : (savedName || tx("fuarAlaniForm.fuarAlani"));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={title} pageTitle={tx("fuarAlaniForm.fuarAlanlari")} />

        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
        {successMsg && <Alert color={successMsg.includes("eklenemedi") ? "warning" : "success"}>{successMsg}</Alert>}

        <div className="d-flex gap-2 mb-3">
          <Link to="/Management/FairAreas" className="btn btn-light">
            <i className="bx bx-arrow-back me-1" />{tx("fuarAlaniForm.fuarAlanlari")}
          </Link>
          {!isNew && (
            <>
              {/* Backlog #51 (2026-09-20): detay artık ayrı bir sayfa — düzenlemeden geri dönüş. */}
              <Link to={`/Management/FairAreas/${fuarAlaniId}`} className="btn btn-soft-secondary">
                <i className="bx bx-show me-1" />{tx("fuarAlaniForm.detayaDon")}
              </Link>
              <Link to={`/Management/FairAreas/${fuarAlaniId}/Salonlar`} className="btn btn-soft-info">
                <i className="bx bx-buildings me-1" />{tx("fuarAlaniForm.salonlarSayfasi")}
              </Link>
            </>
          )}
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <>
            <Card>
              <CardHeader><h6 className="card-title mb-0">{tx("fuarAlaniForm.genelBilgiler")}</h6></CardHeader>
              <CardBody>
                <Row className="g-3 align-items-end">
                  <Col md={6}>
                    <Label for="fuarAlaniName">{tx("fuarAlaniForm.fuarAlaniAdi")}{" "}<span className="text-danger">*</span></Label>
                    <Input
                      id="fuarAlaniName" value={name} placeholder={tx("fuarAlaniForm.ornIstanbulFuarMerkezi")}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Col>
                  <Col md="auto">
                    <Button
                      color="primary" onClick={handleSave}
                      disabled={saving || !name.trim() || (!isNew && name.trim() === savedName)}
                    >
                      {saving ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-save me-1" />}
                      {isNew ? tx("fuarAlaniForm.olustur") : tx("common:kaydet")}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {isNew ? (
              <Card>
                <CardHeader className="d-flex align-items-center">
                  <h6 className="card-title mb-0 flex-grow-1">{tx("fuarAlaniForm.salonlar")}</h6>
                  <span className="text-muted small">{tx("fuarAlaniForm.fuarAlaniylaBirlikteOlusturulur")}</span>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3">{tx("fuarAlaniForm.salonAdi")}</th>
                          <th style={{ width: 170 }}>{tx("fuarAlaniForm.fizikselTavan")}</th>
                          <th style={{ width: 170 }}>{tx("fuarAlaniForm.izinVerilenYukseklik")}</th>
                          <th className="text-end pe-3" style={{ width: 100 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {pendingHalls.map((h, i) => (
                          <tr key={h.salonName}>
                            <td className="ps-3 fw-semibold">{h.salonName}</td>
                            <td>{h.ceilingHeight === "" ? "—" : `${h.ceilingHeight} m`}</td>
                            <td>{h.allowedHMax === "" ? "—" : `${h.allowedHMax} m`}</td>
                            <td className="text-end pe-3">
                              <Button size="sm" color="danger" outline title={tx("fuarAlaniForm.listedenCikar")}
                                onClick={() => setPendingHalls((p) => p.filter((_, j) => j !== i))}>
                                <i className="bx bx-trash" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        <tr className="table-light">
                          <td className="ps-3">
                            <Input bsSize="sm" placeholder={tx("fuarAlaniForm.salonAdi")} value={hallDraft.salonName}
                              onChange={(e) => setHallDraft((p) => ({ ...p, salonName: e.target.value }))} />
                          </td>
                          <td>
                            <InputGroup size="sm">
                              <Input type="number" min="0" step="0.1" placeholder={tx("fuarAlaniForm.tavan")} value={hallDraft.ceilingHeight}
                                onChange={(e) => setHallDraft((p) => ({ ...p, ceilingHeight: e.target.value }))} />
                              <InputGroupText>m</InputGroupText>
                            </InputGroup>
                          </td>
                          <td>
                            <InputGroup size="sm">
                              <Input type="number" min="0" step="0.1" placeholder={tx("fuarAlaniForm.izinVerilen")} value={hallDraft.allowedHMax}
                                invalid={Boolean(draftProblem)}
                                onChange={(e) => setHallDraft((p) => ({ ...p, allowedHMax: e.target.value }))} />
                              <InputGroupText>m</InputGroupText>
                            </InputGroup>
                            {draftProblem && <div className="text-danger small">{draftProblem}</div>}
                          </td>
                          <td className="text-end pe-3">
                            <Button size="sm" color="primary" onClick={addPendingHall}
                              disabled={!hallDraft.salonName.trim() || Boolean(draftProblem)}>
                              <i className="bx bx-plus me-1" />{tx("common:ekle")}
                            </Button>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            ) : (
              !error && <FuarAlaniSalonlariCard fuarAlaniId={fuarAlaniId} />
            )}
          </>
        )}
      </Container>
    </div>
  );
}
